package com.fireavert.yardi.common_data.client

import YardiSavePropertyDataRequest
import com.fireavert.common.Try
import com.fireavert.yardi.common_data.data_models.requests.client_requests.*
import com.fireavert.yardi.common_data.data_models.responses.*
import com.fireavert.yardi.service_requests.data_models.requests.client_requests.YardiClientServiceRequestCreateEditRequest
import com.fireavert.yardi.service_requests.data_models.requests.client_requests.YardiClientServiceRequestGetRequest
import com.fireavert.yardi.service_requests.data_models.responses.YardiServiceRequestCreateEditResponse
import com.fireavert.yardi.service_requests.data_models.responses.YardiServiceRequestGetResponse
import com.fireavert.yardi.service_requests.data_models.responses.YardiServiceRequestPropertyConfigurationsResponse
import com.fireavert.yardi.service_requests.data_models.responses.YardiServiceRequestVersionResponse
import com.fireavert.yardi.shared.logic.data_models.YardiMiniCredentials
import io.ktor.client.*
import io.ktor.client.call.*
import io.ktor.client.plugins.*
import io.ktor.client.request.*
import io.ktor.http.*

class YardiCommonDataClientDataSourceImpl(
    private var baseUrl: String,
    private var httpClient: HttpClient,
): YardiCommonDataClientDataSource {
    override suspend fun getYardiCredentialsList(token: String): Try<List<YardiMiniCredentials>> {
        return Try.fromCoCallable {
            val request = httpClient.get {
                header("Authorization", "Bearer $token")
                url("$baseUrl/yardi/commonData/credentials")
            }
            if (request.status.isSuccess()) request.body()
            else throw ClientRequestException(request, "")
        }
    }

    override suspend fun savePropertyDataBase(token: String, request: YardiSavePropertyDataRequest): Try<Boolean> {
        return Try.fromCoCallable {
            val response = httpClient.post {
                header("Authorization", "Bearer $token")
                url("$baseUrl/yardi/commonData/credentials/save")
                contentType(ContentType.Application.Json)
                setBody(request)
            }
            if (response.status.isSuccess()) true
            else throw ClientRequestException(response, "")
        }
    }

    override suspend fun testPing(token: String, request: YardiClientCredentialRequest): Try<YardiCommonDataClientPingResponse> {
        return Try.fromCoCallable {
            val response = httpClient.post {
                header("Authorization", "Bearer $token")
                url("$baseUrl/yardi/commonData/pingTest")
                contentType(ContentType.Application.Json)
                setBody(request)
            }
            if (response.status.isSuccess()) response.body()
            else throw ClientRequestException(response, "")
        }
    }

    override suspend fun testVersionNumber(
        token: String,
        request: YardiClientCredentialRequest
    ): Try<YardiCommonDataClientVersionResponse> {
        val response = httpClient.post {
            header("Authorization", "Bearer $token")
            url("$baseUrl/yardi/commonData/versionTest")
            contentType(ContentType.Application.Json)
            setBody(request)
        }
        if (response.status.isSuccess()) return Try.Success(response.body())
        else throw ClientRequestException(response, "")
    }

    override suspend fun testGetResidentData(
        token: String,
        request: YardiClientPropertyIdRequest
    ): Try<YardiCommonDataClientResidentResponse> {
        val response = httpClient.post {
            header("Authorization", "Bearer $token")
            url("$baseUrl/yardi/commonData/residentDataTest")
            contentType(ContentType.Application.Json)
            setBody(request)
        }
        if (response.status.isSuccess()) return Try.Success(response.body())
        else throw ClientRequestException(response, "")
    }

    override suspend fun testGetResidentByStatusData(
        token: String,
        request: YardiClientResidentByStatusRequest
    ): Try<YardiCommonDataClientResidentResponse> {
        val response = httpClient.post {
            header("Authorization", "Bearer $token")
            url("$baseUrl/yardi/commonData/testResidentsByStatus")
            contentType(ContentType.Application.Json)
            setBody(request)
        }
        if (response.status.isSuccess()) return Try.Success(response.body())
        else throw ClientRequestException(response, "")
    }

    override suspend fun yardiGetPropertyList(
        token: String,
        request: YardiClientPropertyIdRequest
    ): Try<YardiCommonDataGetPropertyListResponse> {
        val response = httpClient.post {
            header("Authorization", "Bearer $token")
            url("$baseUrl/yardi/commonData/getPropertyList")
            contentType(ContentType.Application.Json)
            setBody(request)
        }
        if (response.status.isSuccess()) return Try.Success(response.body())
        else throw ClientRequestException(response, "")
    }

    override suspend fun testGetResidentsByUnit(
        token: String,
        request: YardiClientGetResidentByUnitRequest
    ): Try<YardiCommonDataGetResidentByUnitResponse> {
        val response = httpClient.post {
            header("Authorization", "Bearer $token")
            url("$baseUrl/yardi/commonData/getResidentsByUnitTest")
            contentType(ContentType.Application.Json)
            setBody(request)
        }
        if (response.status.isSuccess()) return Try.Success(response.body())
        else throw ClientRequestException(response, "")
    }

    override suspend fun testGetTenantStatus(
        token: String,
        request: YardiClientPropertyIdRequest
    ): Try<YardiCommonDataGetTenantsStatusResponse> {
        val response = httpClient.post {
            header("Authorization", "Bearer $token")
            url("$baseUrl/yardi/commonData/getTenantStatusTest")
            contentType(ContentType.Application.Json)
            setBody(request)
        }
        if (response.status.isSuccess()) return Try.Success(response.body())
        else throw ClientRequestException(response, "")
    }

    override suspend fun testGetRateLimits(
        token: String,
        request: YardiClientCredentialRequest
    ): Try<YardiCommonDataGetRateLimitResponse> {
        val response = httpClient.post {
            header("Authorization", "Bearer $token")
            url("$baseUrl/yardi/commonData/rateLimitTest")
            contentType(ContentType.Application.Json)
            setBody(request)
        }
        if (response.status.isSuccess()) return Try.Success(response.body())
        else throw ClientRequestException(response, "")
    }

    override suspend fun testGetUnitAttachments(
        token: String,
        request: YardiClientCredentialRequest
    ): Try<YardiCommonDataClientResidentResponse> {
        val response = httpClient.post {
            header("Authorization", "Bearer $token")
            url("$baseUrl/yardi/commonData/unitAttachmentsTest")
            contentType(ContentType.Application.Json)
            setBody(request)
        }
        if (response.status.isSuccess()) return Try.Success(response.body())
        else throw ClientRequestException(response, "")
    }

    override suspend fun yardiGetUnitData(
        token: String,
        request: YardiClientPropertyIdRequest
    ): Try<YardiCommonDataClientUnitInfoResponse> {
        val response = httpClient.post {
            header("Authorization", "Bearer $token")
            url("$baseUrl/yardi/commonData/getUnitData")
            contentType(ContentType.Application.Json)
            setBody(request)
        }
        if (response.status.isSuccess()) return Try.Success(response.body())
        else throw ClientRequestException(response, "")
    }

    override suspend fun testGetResidentDataResidentialOnly(
        token: String,
        request: YardiClientGetResidentialDataResidentialOnlyRequest
    ): Try<YardiCommonDataClientResidentResponse> {
        val response = httpClient.post {
            header("Authorization", "Bearer $token")
            url("$baseUrl/yardi/commonData/residentDataResidentialOnlyTest")
            contentType(ContentType.Application.Json)
            setBody(request)
        }
        if (response.status.isSuccess()) return Try.Success(response.body())
        else throw ClientRequestException(response, "")
    }

    override suspend fun getPropertyConfigurations(
        token: String,
        request: YardiClientPropertyIdRequest
    ): Try<YardiServiceRequestPropertyConfigurationsResponse> {
        val response = httpClient.post {
            header("Authorization", "Bearer $token")
            url("$baseUrl/yardi/commonData/getPropertyConfigurations")
            contentType(ContentType.Application.Json)
            setBody(request)
        }
        if (response.status.isSuccess()) return Try.Success(response.body())
        else throw ClientRequestException(response, "")
    }

    override suspend fun testGetServiceRequestVersionNumber(
        token: String,
        request: YardiClientCredentialRequest
    ): Try<YardiServiceRequestVersionResponse> = Try.fromCoCallable {
        val response = httpClient.post {
            header("Authorization", "Bearer $token")
            url("$baseUrl/yardi/serviceRequests/versionTest")
            contentType(ContentType.Application.Json)
            setBody(request)
        }
        if (response.status.isSuccess()) response.body()
        else throw ClientRequestException(response, "Service Request Version test failed")
    }

    override suspend fun testGetServiceRequestPropertyConfigurations(
        token: String,
        request: YardiClientPropertyIdRequest
    ): Try<YardiServiceRequestPropertyConfigurationsResponse> = Try.fromCoCallable {
        val response = httpClient.post {
            header("Authorization", "Bearer $token")
            url("$baseUrl/yardi/serviceRequests/propertyConfigurationsTest")
            contentType(ContentType.Application.Json)
            setBody(request)
        }
        if (response.status.isSuccess()) response.body()
        else throw ClientRequestException(response, "Service Request Property Configs test failed")
    }

    override suspend fun testCreateEditServiceRequest(
        token: String,
        request: YardiClientServiceRequestCreateEditRequest
    ): Try<YardiServiceRequestCreateEditResponse> = Try.fromCoCallable {
        val response = httpClient.post {
            header("Authorization", "Bearer $token")
            url("$baseUrl/yardi/serviceRequests/createEditTest")
            contentType(ContentType.Application.Json)
            setBody(request)
        }
        if (response.status.isSuccess()) response.body()
        else throw ClientRequestException(response, response.body())
    }

    override suspend fun testGetServiceRequests(
        token: String,
        request: YardiClientServiceRequestGetRequest
    ): Try<YardiServiceRequestGetResponse> = Try.fromCoCallable {
        val response = httpClient.post {
            header("Authorization", "Bearer $token")
            url("$baseUrl/yardi/serviceRequests/getServiceRequestsTest")
            contentType(ContentType.Application.Json)
            setBody(request)
        }
        if (response.status.isSuccess()) response.body()
        else throw ClientRequestException(response, "Service Request Get test failed")
    }

    override suspend fun importYardiUnitData(
        token: String,
        request: YardiClientImportUnitData
    ): Try<YardiImportUnitDataResponse> {
        return Try.fromCoCallable {
            val response = httpClient.post {
                header("Authorization", "Bearer $token")
                url("$baseUrl/yardi/commonData/importUnitData")
                contentType(ContentType.Application.Json)
                setBody(request)
            }
            if (response.status.isSuccess()) response.body()
            else throw ClientRequestException(response, "Import Yardi Unit Data failed")
        }
    }
}